import { ManageCompanyTab } from 'components/company/manageCompanyNavConstants';
import { CompilerStep1Tab, CompilerStep2Tab, CompilerStep3Tab } from 'components/v2/compiler/compilerNavConstants';

const EpdRoutes = {
  welcome: '/',
  login: '/login',
  notifications: '/notifications',
  epdCompilerPdfPreview: '/epdCompilerPdfPreview/:id',
  epdCompilerPdfPreviewAuth: '/epdCompilerPdfPreview/Auth/:id',
  epdRegistrationCertificatePdfPreviewAuth: '/epdRegistrationCertificatePdfPreview/Auth/:id',
  epdRegistrationCertificatePdfPreview: '/epdRegistrationCertificatePdfPreview/:id',
  dynamicFormRegistrationCertificatePdfPreviewAuth: '/dynamicFormRegistrationCertificatePdfPreview/Auth/:id',
  dynamicFormRegistrationCertificatePdfPreview: '/dynamicFormRegistrationCertificatePdfPreview/:id',
  epdCompilerDocumentPreview: '/epdCompilerDocumentPreview/:id',
  dashboard: '/dashboard',
  epd: '/epd/:id?',
  epdVersionHistory: '/epd/:epdId/history/:versionId',
  epdWizardVersionHistory: '/epd/:epdId/wizard-history/:versionId',
  epdCommentLog: '/epd/:epdId/comment-log',
  epdVersionCompare: '/epd/:epdId/compare/:versionAId/:versionBId',
  dynamicForm: '/dynamic-forms/:docId',
  dynamicFormCommentLog: '/dynamic-forms/:docId/comment-log',
  dynamicFormVersionLog: '/dynamic-forms/:docId/versions/:versionId',
  dynamicFormVersionCompare: '/dynamic-forms/:docId/compare/:versionAId/:versionBId',
  invoice: '/invoice/:companyid/:accountId/:invoiceId',
  invoices: '/invoices/:companyid/:accountId',

  selectCompany: '/company/:companyId/organizations',
  selectCompanyEmpty: '/company//organizations',

  collections: '/collections',
  editCollection: '/collection/:collectionId',

  copyEpd: '/copyEpd',
  editCopyEpd: '/copyEpd/:epdGroupId',

  account: {
    register: {
      user: '/register/user',
      userError: '/register/user/error',
      userSuccess: '/register/user/success',
      company: '/register/organisation/:tab?',
    },
    forgotPassword: '/account/forgotpassword',
    forgotPasswordSuccess: '/account/forgotpassword/success',
    resetPassword: '/account/resetpassword',
    resetPasswordSuccess: '/account/resetpassword/success',
    resetPasswordTokenError: '/account/resetpassword/tokenerror',
    confirmEmail: '/account/confirmemail',
    userSettings: '/account/usersettings/:tab?/:subTab?',
  },
  admin: {
    users: {
      users: '/admin/users',
      editUser: '/admin/users/:userid',
    },
    companies: {
      search: '/admin/organisations',
      editCompany: '/admin/organisations/:companyid',
    },
    invoices: {
      invoices: '/admin/invoices',
      editInvoice: '/admin/invoices/:invoiceid',
    },
    epds: {
      search: '/admin/epds/search',
      editEpd: '/admin/epds/:epdid',
      showGroupEpd: '/admin/groupepds/:epdid',
    },
    licensees: {
      licensees: '/admin/licensees',
    },
    collocationCenters: '/admin/collocation-centers',
    pcrs: {
      pcrs: '/admin/pcrs',
      editPcr: '/admin/pcrs/:pcrid',
    },
    tools: {
      search: '/admin/tools',
      editTool: '/admin/tools/:id',
    },
    dataSources: {
      dataSources: '/admin/dataSources',
      editDataSource: '/admin/dataSources/:id',
    },
    dictionaries: {
      dictionaries: '/admin/dictionaries',
    },
    kpis: {
      summary: '/admin/kpis/summary',
    },
    verifiers: {
      summary: '/admin/verifiers/summary',
    },
    epdsreport: {
      summary: '/admin/epdsreport/summary',
    },
    integration: {
      ilcdEpd: '/admin/integration/ilcdepd',
      ilcdEpdMapping: '/admin/integration/ilcdepd/:id',
      ilcdEpdMappingList: '/admin/integration/ilcdepdlist/:id',
      fortnoxIntegrationPage: '/admin/integration/fortnox',
      FortnoxInvoicesPage: '/admin/integration/fortnox/invoices',
    },
    system: {
      validationRules: '/admin/system/validation-rules',
      lcaSoftware: '/admin/system/lca-software',
      notifications: '/admin/system/notifications',
      featureFlags: '/admin/system/feature-flags',
    },
  },
  lcaValidation: '/lca/validation',
  embedLibrary: '/embed/library',
  embedLibraryCarousel: '/embed/library-carousel',
  resources: {
    contactVerifier: '/contact-verifier',
  },
  forbidden: '/forbidden',
  notFound: '/not-found',
  param: (route: string, id: string, value: string) => {
    return route.replace(id, value); //TODO: Use EpdLinks instead?
  },
};

export const EpdLinks = {
  dashboard: () => {
    return EpdRoutes.dashboard;
  },
  manageCompany: (companyId: string, tab?: ManageCompanyTab | ':tab?') => {
    return `/manage/${companyId}${!tab ? '' : '/' + tab}`;
  },
  manageCompanyTool: (companyId: string, toolId: string) => `/manage/${companyId}/tools/${toolId}`,
  selectCompany: (companyId: string) => {
    return EpdRoutes.param(EpdRoutes.selectCompany, ':companyId', companyId);
  },
  epdVersionPdfPreview: (id: string) => {
    return EpdRoutes.param(EpdRoutes.epdCompilerPdfPreview, ':id', id);
  },
  epdVersionDocumentPreview: (id: string) => {
    return EpdRoutes.param(EpdRoutes.epdCompilerDocumentPreview, ':id', id);
  },
  epd: (id?: string) => {
    return EpdRoutes.param(EpdRoutes.epd, ':id?', id || '');
  },
  epdCompilerSpecific: (
    id: string,
    destination:
      | {
          step: 'step1';
          tab: CompilerStep1Tab;
        }
      | {
          step: 'step2';
          tab: CompilerStep2Tab;
        }
      | {
          step: 'step3';
          tab: CompilerStep3Tab;
        }
      | {
          step: 'step4' | 'step5' | 'step6';
          tab?: undefined;
        }
  ) => {
    const queryParams = destination.tab ? `?tab=${destination.tab}` : '';
    return `${EpdRoutes.param(EpdRoutes.epd, ':id?', id || '')}/${destination.step}${queryParams}`;
  },
  epdVersionHistory: (epdId: string, versionId: string) => {
    let route = EpdRoutes.epdVersionHistory;
    route = EpdRoutes.param(route, ':epdId', epdId);
    route = EpdRoutes.param(route, ':versionId', versionId);
    return route;
  },
  epdWizardVersionHistory: (epdId: string, versionId: string) => {
    let route = EpdRoutes.epdWizardVersionHistory;
    route = EpdRoutes.param(route, ':epdId', epdId);
    route = EpdRoutes.param(route, ':versionId', versionId);
    return route;
  },
  epdCommentLog: (epdId: string) => {
    return EpdRoutes.param(EpdRoutes.epdCommentLog, ':epdId', epdId);
  },
  dynamicForm: (docId: string) => {
    return EpdRoutes.param(EpdRoutes.dynamicForm, ':docId', docId);
  },
  dynamicFormVersionLog: (docId: string, versionId: string) => {
    let route = EpdRoutes.dynamicFormVersionLog;
    route = EpdRoutes.param(route, ':docId', docId);
    route = EpdRoutes.param(route, ':versionId', versionId);
    return route;
  },
  epdVersionCompare: (epdId: string, versionAId: string, versionBId: string) => {
    let route = EpdRoutes.epdVersionCompare;
    route = EpdRoutes.param(route, ':epdId', epdId);
    route = EpdRoutes.param(route, ':versionAId', versionAId);
    route = EpdRoutes.param(route, ':versionBId', versionBId);
    return route;
  },

  epdCompilerPdfPreviewAuth: (id?: string) => {
    return EpdRoutes.param(EpdRoutes.epdCompilerPdfPreviewAuth, ':id', id || '');
  },

  epdRegistrationCertificatePdfPreviewAuth: (id?: string) => {
    return EpdRoutes.param(EpdRoutes.epdRegistrationCertificatePdfPreviewAuth, ':id', id || '');
  },

  dynamicFormRegistrationCertificatePdfPreviewAuth: (id?: string) => {
    return EpdRoutes.param(EpdRoutes.dynamicFormRegistrationCertificatePdfPreviewAuth, ':id', id || '');
  },

  invoices: (companyId: string, accountId: string) => {
    const route = EpdRoutes.param(EpdRoutes.invoices, ':companyid', companyId);
    return EpdRoutes.param(route, ':accountId', accountId);
  },
  invoice: (companyId: string, accountId: string, invoiceId: string) => {
    let route = EpdRoutes.param(EpdRoutes.invoice, ':companyid', companyId);
    route = EpdRoutes.param(route, ':accountId', accountId);
    return EpdRoutes.param(route, ':invoiceId', invoiceId);
  },
  editCompany: (companyId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.companies.editCompany, ':companyid', companyId);
  },
  editPcr: (pcrId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.pcrs.editPcr, ':pcrid', pcrId);
  },
  editTool: (id: string) => {
    return EpdRoutes.param(EpdRoutes.admin.tools.editTool, ':id', id);
  },
  editDataSource: (id: string) => {
    return EpdRoutes.param(EpdRoutes.admin.dataSources.editDataSource, ':id', id);
  },
  editInvoice: (invoiceId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.invoices.editInvoice, ':invoiceid', invoiceId);
  },
  epdsList: () => {
    return EpdRoutes.admin.epds.search;
  },
  editEpd: (epdId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.epds.editEpd, ':epdid', epdId);
  },
  showGroupEpd: (epdId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.epds.showGroupEpd, ':epdid', epdId);
  },
  editUser: (userId: string) => {
    return EpdRoutes.param(EpdRoutes.admin.users.editUser, ':userid', userId);
  },

  //below routes for the v2 UI
  collections: () => {
    return EpdRoutes.collections;
  },
  editCollection: (collectionId: string) => {
    return EpdRoutes.param(EpdRoutes.editCollection, ':collectionId', collectionId);
  },
  ilcdEpdMapping: (id: string) => {
    return EpdRoutes.param(EpdRoutes.admin.integration.ilcdEpdMapping, ':id', id);
  },
  ilcdEpdMappingList: (id: string) => {
    return EpdRoutes.param(EpdRoutes.admin.integration.ilcdEpdMappingList, ':id', id);
  },
  FortnoxIntegrationPage: () => {
    return EpdRoutes.admin.integration.fortnoxIntegrationPage;
  },
  editCopyEpd: (epdGroupId: string) => {
    return EpdRoutes.param(EpdRoutes.editCopyEpd, ':epdGroupId', epdGroupId);
  },
  lcaValidation: () => {
    return EpdRoutes.lcaValidation;
  },

  userSettings: (tab?: 'account' | 'membership', subTab?: 'myRoles' | 'myRequests' | 'invites') => {
    let route = EpdRoutes.account.userSettings;

    const tabSegment = tab ? `/${tab}` : '';
    route = EpdRoutes.param(route, '/:tab?', tabSegment);

    const subTabSegment = tab === 'membership' && subTab ? `/${subTab}` : '';
    route = EpdRoutes.param(route, '/:subTab?', subTabSegment);

    return route;
  },

  organisation: (tab?: 'connect' | 'create') => {
    let route = EpdRoutes.account.register.company;
    const tabSegment = tab ? `/${tab}` : '';
    route = EpdRoutes.param(route, '/:tab?', tabSegment);

    return route;
  },

  dynamicFormEpdVersionLog: (docId: string, versionId: string) => {
    let route = EpdRoutes.dynamicFormVersionLog;
    route = EpdRoutes.param(route, ':docId', docId);
    route = EpdRoutes.param(route, ':versionId', versionId);

    return route;
  },

  dynamicFormEpdVersionCompare: (docId: string, versionAId: string, versionBId: string) => {
    let route = EpdRoutes.dynamicFormVersionCompare;
    route = EpdRoutes.param(route, ':docId', docId);
    route = EpdRoutes.param(route, ':versionAId', versionAId);
    route = EpdRoutes.param(route, ':versionBId', versionBId);

    return route;
  },
};

export default EpdRoutes;
